// Pure parsers for scene.json. No fetch, no THREE state mutation — these
// turn raw JSON values into typed shapes (or null when the input is missing).

import * as THREE from 'three';

export type SceneJson = Record<string, unknown>;
export type SceneObjectSpec = Record<string, unknown>;

export function asObj(v: unknown): Record<string, unknown> | null {
    return (v && typeof v === 'object' && !Array.isArray(v)) ? v as Record<string, unknown> : null;
}
export function asNum(v: unknown): number | undefined { return typeof v === 'number' ? v : undefined; }
export function asStr(v: unknown): string | undefined { return typeof v === 'string' ? v : undefined; }
export function asBool(v: unknown): boolean | undefined { return typeof v === 'boolean' ? v : undefined; }

/**
 * Convert a scene.json `rotation` block into a THREE.Matrix3. Accepts three
 * forms (in priority order):
 *   1. Explicit row-major 3x3 matrix.
 *   2. Quaternion { x, y, z, w }.
 *   3. Euler { yaw, pitch, roll, order?, degrees? }
 * Returns null if the input is missing or unrecognized.
 */
export function parseRotationToMatrix3(rotationConf: unknown): THREE.Matrix3 | null {
    const r = asObj(rotationConf);
    if (!r) return null;

    if (Array.isArray(r['matrix']) && (r['matrix'] as unknown[]).length === 3 &&
        (r['matrix'] as unknown[]).every((row) => Array.isArray(row) && (row as unknown[]).length === 3)) {
        const m = r['matrix'] as number[][];
        return new THREE.Matrix3().set(
            m[0]![0]!, m[0]![1]!, m[0]![2]!,
            m[1]![0]!, m[1]![1]!, m[1]![2]!,
            m[2]![0]!, m[2]![1]!, m[2]![2]!,
        );
    }

    const q = asObj(r['quaternion']);
    if (q && ['x', 'y', 'z', 'w'].every((k) => typeof q[k] === 'number')) {
        const quat = new THREE.Quaternion(q['x'] as number, q['y'] as number, q['z'] as number, q['w'] as number);
        const m4 = new THREE.Matrix4().makeRotationFromQuaternion(quat);
        return new THREE.Matrix3().setFromMatrix4(m4);
    }

    const e = asObj(r['euler']);
    if (e) {
        const order = typeof e['order'] === 'string' ? e['order'] as THREE.EulerOrder : 'ZYX';
        const isDegrees = e['degrees'] !== false;
        const toRad = isDegrees ? Math.PI / 180 : 1;
        const yaw = ((e['yaw'] as number) || 0) * toRad;
        const pitch = ((e['pitch'] as number) || 0) * toRad;
        const roll = ((e['roll'] as number) || 0) * toRad;
        const euler = new THREE.Euler(roll, pitch, yaw, order);
        const m4 = new THREE.Matrix4().makeRotationFromEuler(euler);
        return new THREE.Matrix3().setFromMatrix4(m4);
    }

    return null;
}
